import { Dayjs } from "dayjs";
import { api } from "../../../config/api";
import { useQuery } from "@tanstack/react-query";

export interface DashboardData {
  todays_shipments: number;
  todays_revenue: number;
  average_shipping: number;
  shipments_details: {
    total_shipments: number;
    pickup_pending: number;
    in_transit: number;
    exception: number;
    delivered: number;
    rto: number;
  };
  cod_status: {
    total_cod: number;
    cod_pending: number;
    cod_remitted: number;
  };
  shipments_by_service: [
    {
      service: string;
      total_shipments: number;
      pickup_unscheduled: number;
      booking_pending: number;
      pickup_scheduled: number;
      in_transit: number;
      exception: number;
      delivered: number;
      rto: number;
    }
  ];
}

export const getDashboardDetails = () => {
  return api.get("/b2c/dashboard/");
};

export const getShipmentsDetails = (
  paymentMode: string,
  startDate: Dayjs,
  endDate: Dayjs
) => {
  const from_date = startDate.format("YYYY-MM-DD");
  const to_date = endDate.format("YYYY-MM-DD");

  return api.get("/b2c/dashboard/service/", {
    params: {
      payment_mode: paymentMode,
      from_date,
      to_date,
    },
  });
};

export const getProductsDetails = (startDate: Dayjs, endDate: Dayjs) => {
  const from_date = startDate.format("YYYY-MM-DD");
  const to_date = endDate.format("YYYY-MM-DD");

  return api.get("/b2c/dashboard/orders/products/", {
    params: {
      from_date,
      to_date,
    },
  });
};

export const getReturnsDetails = (
  paymentMode: string,
  startDate: Dayjs,
  endDate: Dayjs,
  service: string
) => {
  const from_date = startDate.format("YYYY-MM-DD");
  const to_date = endDate.format("YYYY-MM-DD");
  return api.get("/b2c/dashboard/return/", {
    params: {
      from_date,
      to_date,
      payment_mode: paymentMode,
      service,
    },
  });
};

export function useOverview() {
  const {
    isLoading,
    data: overview,
    error,
  } = useQuery({
    queryKey: ["overview"],
    queryFn: getDashboardDetails,
  });

  const data = overview?.data?.data as DashboardData;

  return { isLoading, error, data };
}

interface ShipmentDetails {
  service: string;
  total_shipments: number;
  pickup_unscheduled: number;
  booking_pending: number;
  pickup_scheduled: number;
  in_transit: number;
  exception: number;
  delivered: number;
  rto: number;
}

export function useShipments(
  paymentMode: string,
  startDate: Dayjs,
  endDate: Dayjs
) {
  const {
    isLoading,
    data: shipments,
    error,
  } = useQuery({
    queryKey: [
      `shipments`,
      paymentMode,
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD"),
    ],
    queryFn: () => getShipmentsDetails(paymentMode, startDate, endDate),
    retry: false,
  });

  const data = shipments?.data?.data as ShipmentDetails[];

  return { isLoading, error, data };
}

interface ReturnsData {
  total_return: number;
  return_percentage: number;
  pickup_unscheduled: number;
  pickup_scheduled: number;
  in_transit: number;
  exception: number;
  delivered: number;
  return_count: [
    {
      date: string;
      total_orders: number;
      pickup_unscheduled: number;
      pickup_scheduled: number;
      in_transit: number;
      exception: number;
      cancelled: number;
      delivered: number;
    }
  ];
  return_status: [];
}

export function useReturns(
  paymentMode: string,
  startDate: Dayjs,
  endDate: Dayjs,
  service: string
) {
  const {
    isLoading,
    data: returns,
    error,
  } = useQuery({
    queryKey: [
      `returns-dashboard`,
      paymentMode,
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD"),
      service,
    ],
    queryFn: () => getReturnsDetails(paymentMode, startDate, endDate, service),
    retry: false,
  });

  const data = returns?.data?.data as ReturnsData;

  return { isLoading, error, data };
}

interface listDetails {
  prepaid_prders: number;
  cod_orders: number;
  top_10_cities: string[];
  orders_by_date: ListDataNewType[];
}

interface ListDataNewType {
  date: string;
  total_orders: number;
  pickup_unscheduled: number;
  pickup_scheduled: number;
  in_transit: number;
  delivered: number;
  rto: number;
  cancelled: number;
  exception: number;
}

export interface ProductDataType {
  product_name: string;
  unit_sold: number;
  revenue: number;
}

export const getOrderDetails = (
  paymentMode: string,
  serviceMode: string,
  shipmentMode: string,
  startDate: Dayjs,
  endDate: Dayjs
) => {
  const from_date = startDate.format("YYYY-MM-DD");
  const to_date = endDate.format("YYYY-MM-DD");

  return api.get("/b2c/dashboard/orders/", {
    params: {
      payment_mode: paymentMode,
      service: serviceMode,
      shipment_mode: shipmentMode,
      from_date,
      to_date,
    },
  });
};

export function useOrderdata(
  paymentMode: string,
  serviceMode: string,
  shipmentMode: string,
  startDate: Dayjs,
  endDate: Dayjs
) {
  const {
    isLoading,
    data: List,
    error,
  } = useQuery({
    queryKey: [
      `dashboard-oders`,
      serviceMode,
      paymentMode,
      shipmentMode,
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD"),
      serviceMode,
      shipmentMode,
    ],
    queryFn: () =>
      getOrderDetails(
        paymentMode,
        serviceMode,
        shipmentMode,
        startDate,
        endDate
      ),
    retry: false,
  });

  const data = List?.data?.data as listDetails;

  return { isLoading, error, data };
}

export interface ListDataTypeForCustomer {
  order_count: number;
  person_name: string;
  revenue: number;
}

export const getCustomerList = (startDate: Dayjs, endDate: Dayjs) => {
  const from_date = startDate.format("YYYY-MM-DD");
  const to_date = endDate.format("YYYY-MM-DD");

  return api.get("b2c/dashboard/orders/customers/", {
    params: {
      from_date,
      to_date,
    },
  });
};
